import { type TFunction } from "i18next";
import { Badge } from "~/atoms/badge";
import { Checkbox } from "~/atoms/checkbox";
import { EmptyCellValue } from "~/atoms/table";
import { type BulkSelection } from "~/hooks/useBulkSelection";
import { type ApplyTemplateRow } from "~/hooks/useApplyTemplateRows";
import { type TableColumn } from "~/molecules/SimpleDataTable";
import { TagsValue } from "~/molecules/Tags";

interface Params {
  /** The template being applied; its rows sort first and get a solid badge. */
  templateId: number | null;
  tagMap: Map<number, string>;
  selection: BulkSelection<number>;
  t: TFunction;
}

export function buildApplyTemplateColumns({
  templateId,
  tagMap,
  selection,
  t,
}: Params): TableColumn<ApplyTemplateRow>[] {
  return [
    {
      header: (
        <Checkbox
          checked={selection.allSelected}
          onCheckedChange={selection.toggleAll}
          aria-label={t(($) => $.commons.buttons.selectAll)}
        />
      ),
      cell: (row) => (
        <Checkbox
          checked={selection.selectedIds.has(row.id)}
          onCheckedChange={() => selection.toggleRow(row.id)}
          aria-label={t(($) => $.commons.buttons.selectItem, {
            name: row.title,
          })}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      ),
      tableHeadClassName: "w-12",
    },
    {
      header: t(($) => $.workflowTemplates.documentsTable.documentTitle),
      compareFn: (a, b) => a.title.localeCompare(b.title),
      cell: (row) => <span className="font-medium">{row.title}</span>,
    },
    {
      header: t(($) => $.workflowTemplates.documentsTable.currentTemplate),
      defaultSort: true,
      compareFn: (a, b) => {
        const aMatch = a.currentTemplateId === templateId ? 1 : 0;
        const bMatch = b.currentTemplateId === templateId ? 1 : 0;
        if (aMatch !== bMatch) return bMatch - aMatch;
        return a.title.localeCompare(b.title);
      },
      cell: (row) =>
        row.currentTemplateId != null ? (
          <Badge
            variant={
              row.currentTemplateId === templateId ? "default" : "outline"
            }
          >
            {row.currentTemplateName ??
              t(($) => $.workflowTemplates.defaultBadge)}
          </Badge>
        ) : (
          <EmptyCellValue />
        ),
    },
    {
      header: t(($) => $.commons.document.tier),
      compareFn: (a, b) => (a.tier ?? "").localeCompare(b.tier ?? ""),
      cell: (row) => (row.tier ? <span>{row.tier}</span> : <EmptyCellValue />),
    },
    {
      header: t(($) => $.commons.document.category),
      compareFn: (a, b) => a.category.title.localeCompare(b.category.title),
      cell: (row) => <span>{row.category.title}</span>,
    },
    {
      header: t(($) => $.commons.document.owner),
      compareFn: (a, b) => a.ownerName.localeCompare(b.ownerName),
      cell: (row) => <span>{row.ownerName}</span>,
    },
    {
      header: t(($) => $.commons.document.tags),
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.tagIds.length === 0 ? (
            <EmptyCellValue />
          ) : (
            row.tagIds.map((tagId) => (
              <TagsValue key={tagId}>{tagMap.get(tagId) ?? tagId}</TagsValue>
            ))
          )}
        </div>
      ),
    },
  ];
}
